import { NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, BedDouble, CalendarDays, CalendarRange,
  Users, Receipt, Sparkles, Wrench, Car, BarChart3,
  UserCog, Settings, Wallet, Globe, Shield,
  ChevronDown, Building2, Check, Trash2, AlertTriangle, Plus, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHotel } from '@/context/HotelContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import logoImg from '@/assets/logo.png';

const BLANK_HOTEL = { name: '', address: '', city: '', country: '', phone: '', email: '', currency: 'EUR', timezone: 'Europe/Istanbul' };

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  { path: '/calendar', icon: CalendarRange, label: 'Calendar', module: 'calendar' },
  { path: '/rooms', icon: BedDouble, label: 'Rooms', module: 'rooms' },
  { path: '/reservations', icon: CalendarDays, label: 'Reservations', module: 'reservations' },
  { path: '/guests', icon: Users, label: 'Guests', module: 'guests' },
  { path: '/billing', icon: Receipt, label: 'Billing', module: 'billing' },
  { path: '/expenses', icon: Wallet, label: 'Expenses', module: 'expenses' },
  { path: '/housekeeping', icon: Sparkles, label: 'Housekeeping', module: 'housekeeping' },
  { path: '/maintenance', icon: Wrench, label: 'Maintenance', module: 'maintenance' },
  { path: '/transfers', icon: Car, label: 'Transfers', module: 'transfers' },
  { path: '/channels', icon: Globe, label: 'Channels', module: 'channels' },
  { path: '/reports', icon: BarChart3, label: 'Reports', module: 'reports' },
  { path: '/staff', icon: UserCog, label: 'Staff', module: 'staff' },
  { path: '/settings', icon: Settings, label: 'Settings', module: 'settings' },
];

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { hotel, myHotels, activeHotelId, switchHotel, deleteHotel, createHotel, hotelSettings } = useHotel();
  const { user } = useAuth();
  const [hotelDropdownOpen, setHotelDropdownOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAddHotel, setShowAddHotel] = useState(false);
  const [newHotelForm, setNewHotelForm] = useState(BLANK_HOTEL);
  const [creating, setCreating] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setHotelDropdownOpen(false);
    setConfirmDeleteId(null);
    if (onClose) onClose();
  }, [location.pathname]);

  useState(() => { setHotelDropdownOpen(false); setConfirmDeleteId(null); }, [location.pathname]);

  const handleDeleteHotel = async (e, hotelId) => {
    e.stopPropagation();
    if (deleting) return;
    setDeleting(true);
    const ok = await deleteHotel(hotelId);
    setDeleting(false);
    setConfirmDeleteId(null);
    if (ok) toast.success('Hotel deleted successfully');
    else toast.error('Failed to delete hotel');
  };

  const handleCreateHotel = async (e) => {
    e.preventDefault();
    setCreating(true);
    const result = await createHotel(newHotelForm);
    setCreating(false);
    if (result.ok) {
      toast.success(`"${result.hotel.name}" created and activated`);
      setShowAddHotel(false);
      setHotelDropdownOpen(false);
      setNewHotelForm(BLANK_HOTEL);
    } else {
      toast.error(result.error);
    }
  };
  
  // Get all module IDs
  const allModuleIds = navItems.map(i => i.module);
  
  // Get visible modules from hotel settings - if not set or empty, show all modules
  const visibleModules = (hotelSettings?.visible_modules && hotelSettings.visible_modules.length > 0) 
    ? hotelSettings.visible_modules 
    : allModuleIds;
  
  // Get user permissions from their role
  const userPermissions = user?.permissions || {};
  
  // Filter nav items based on:
  // 1. Hotel settings (module visibility) 
  // 2. User role permissions (user must have at least 'view' permission for the module)
  const filteredNavItems = navItems.filter(item => {
    // Check if module is visible in hotel settings
    const isVisibleInSettings = visibleModules.includes(item.module);
    
    // Check if user has permission for this module (at least 'view')
    const userModulePermissions = userPermissions[item.module] || [];
    const hasPermission = user?.role === 'admin' || user?.role === 'super_admin' || userModulePermissions.includes('view');
    
    return isVisibleInSettings && hasPermission;
  });

  return (
    <>
    <aside className={cn(
      "w-64 sidebar-dark flex flex-col border-r border-slate-800 flex-shrink-0",
      "fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
      isOpen ? "translate-x-0" : "-translate-x-full"
    )}>
      <div className="h-14 sm:h-16 flex items-center justify-between px-4 sm:px-6 border-b border-slate-800">
        <div className="flex items-center">
          <img src={logoImg} alt="HotelERP Logo" className="h-8 w-8 sm:h-9 sm:w-9 object-contain" />
          <span className="ml-2 sm:ml-3 text-base sm:text-lg font-semibold font-['Outfit'] text-white">
            HotelERP
          </span>
        </div>
        {/* Close button for mobile */}
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          data-testid="close-sidebar-btn"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {hotel && (
        <div className="px-3 py-2 border-b border-slate-800 relative">
          <button
            onClick={() => setHotelDropdownOpen(o => !o)}
            data-testid="hotel-switcher-btn"
            className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-left transition-colors hover:bg-slate-800 cursor-pointer"
          >
            <div className="flex-shrink-0 w-7 h-7 rounded bg-blue-600/20 flex items-center justify-center">
              <Building2 className="h-4 w-4 text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 leading-none mb-0.5">Current Hotel</p>
              <p className="text-sm font-medium text-white truncate">{hotel.name}</p>
            </div>
            <ChevronDown className={cn("h-4 w-4 text-slate-400 flex-shrink-0 transition-transform", hotelDropdownOpen && "rotate-180")} />
          </button>

          {hotelDropdownOpen && (
            <div className="absolute left-3 right-3 top-full mt-1 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
              <p className="text-xs text-slate-500 uppercase tracking-wider px-3 pt-2 pb-1">Switch Hotel</p>
              {myHotels.map(h => (
                <div key={h.id}>
                  {confirmDeleteId === h.id ? (
                    /* Inline delete confirmation */
                    <div className="px-3 py-2.5 bg-red-950/40 border-t border-red-900/40">
                      <div className="flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
                        <p className="text-xs text-red-300 font-medium leading-tight">Delete "{h.name}"?</p>
                      </div>
                      <p className="text-xs text-slate-500 mb-2.5">All rooms &amp; data will be removed.</p>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                          className="flex-1 text-xs py-1.5 rounded bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
                          data-testid={`cancel-delete-${h.id}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={(e) => handleDeleteHotel(e, h.id)}
                          disabled={deleting}
                          className="flex-1 text-xs py-1.5 rounded bg-red-600 text-white hover:bg-red-500 transition-colors disabled:opacity-50"
                          data-testid={`confirm-delete-${h.id}`}
                        >
                          {deleting ? '...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Normal hotel row */
                    <div className="flex items-center group">
                      <button
                        data-testid={`hotel-option-${h.id}`}
                        onClick={() => { switchHotel(h.id); setHotelDropdownOpen(false); }}
                        className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-800 text-left transition-colors flex-1 min-w-0"
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded bg-slate-700 flex items-center justify-center">
                          <Building2 className="h-3.5 w-3.5 text-slate-300" />
                        </div>
                        <span className={cn("text-sm flex-1 truncate", h.id === activeHotelId ? "text-blue-400 font-medium" : "text-slate-300")}>
                          {h.name}
                        </span>
                        {h.id === activeHotelId && <Check className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />}
                      </button>
                      {/* Only allow delete if user has more than 1 hotel */}
                      {myHotels.length > 1 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(h.id); }}
                          data-testid={`delete-hotel-${h.id}`}
                          title="Delete hotel"
                          className="px-2.5 py-2.5 text-slate-600 hover:text-red-400 hover:bg-slate-800 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {/* Add New Hotel button */}
              <button
                onClick={() => { setShowAddHotel(true); setHotelDropdownOpen(false); }}
                data-testid="add-hotel-btn"
                className="w-full flex items-center gap-2 px-3 py-2.5 text-blue-400 hover:bg-slate-800 transition-colors border-t border-slate-700/50 mt-1"
              >
                <Plus className="h-3.5 w-3.5" />
                <span className="text-sm font-medium">Add New Hotel</span>
              </button>
            </div>
          )}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200",
                    isActive 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-300 hover:text-white hover:bg-slate-800"
                  )}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
        
        {/* Super Admin Link */}
        {user?.role === 'super_admin' && (
          <div className="mt-4 pt-4 border-t border-slate-700 px-3 space-y-2">
            <NavLink
              to="/super-admin"
              data-testid="nav-super-admin"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200",
                location.pathname === '/super-admin'
                  ? "bg-purple-600 text-white" 
                  : "text-purple-400 hover:text-purple-300 hover:bg-slate-800"
              )}
            >
              <Shield className="h-5 w-5" />
              Super Admin
            </NavLink>
            <NavLink
              to="/platform-admin"
              data-testid="nav-platform-admin"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200",
                location.pathname === '/platform-admin'
                  ? "bg-amber-600 text-white" 
                  : "text-amber-400 hover:text-amber-300 hover:bg-slate-800"
              )}
            >
              <UserCog className="h-5 w-5" />
              Platform Admin
            </NavLink>
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <p className="text-xs text-slate-500 text-center">
          © {new Date().getFullYear()} HotelERP Pro
        </p>
      </div>
    </aside>

    {/* Add New Hotel Modal */}
    {showAddHotel && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAddHotel(false)} />
        <div className="relative bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-blue-400" />
              </div>
              <h2 className="text-white font-semibold">Add New Hotel</h2>
            </div>
            <button onClick={() => setShowAddHotel(false)} className="text-slate-400 hover:text-white transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleCreateHotel} className="p-5 space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Hotel Name *</label>
              <input
                required
                value={newHotelForm.name}
                onChange={e => setNewHotelForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Grand Palace Hotel"
                data-testid="new-hotel-name"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">City *</label>
                <input
                  required
                  value={newHotelForm.city}
                  onChange={e => setNewHotelForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="Istanbul"
                  data-testid="new-hotel-city"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Country *</label>
                <input
                  required
                  value={newHotelForm.country}
                  onChange={e => setNewHotelForm(f => ({ ...f, country: e.target.value }))}
                  placeholder="TR"
                  data-testid="new-hotel-country"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Address *</label>
              <input
                required
                value={newHotelForm.address}
                onChange={e => setNewHotelForm(f => ({ ...f, address: e.target.value }))}
                placeholder="123 Main Street"
                data-testid="new-hotel-address"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Phone *</label>
                <input
                  required
                  value={newHotelForm.phone}
                  onChange={e => setNewHotelForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+90 555 000 0000"
                  data-testid="new-hotel-phone"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Email *</label>
                <input
                  required
                  type="email"
                  value={newHotelForm.email}
                  onChange={e => setNewHotelForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="info@hotel.com"
                  data-testid="new-hotel-email"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Currency</label>
                <select
                  value={newHotelForm.currency}
                  onChange={e => setNewHotelForm(f => ({ ...f, currency: e.target.value }))}
                  data-testid="new-hotel-currency"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="TRY">TRY (₺)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Timezone</label>
                <select
                  value={newHotelForm.timezone}
                  onChange={e => setNewHotelForm(f => ({ ...f, timezone: e.target.value }))}
                  data-testid="new-hotel-timezone"
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="Europe/Istanbul">Istanbul (UTC+3)</option>
                  <option value="Europe/London">London (UTC+0)</option>
                  <option value="Europe/Paris">Paris (UTC+1)</option>
                  <option value="America/New_York">New York (UTC-5)</option>
                  <option value="Asia/Dubai">Dubai (UTC+4)</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAddHotel(false)}
                className="flex-1 py-2.5 rounded-lg border border-slate-600 text-slate-300 text-sm hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                data-testid="create-hotel-submit"
                className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Hotel'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
  </>
  );
};

export default Sidebar;
